import React from "react";
import {
  Box,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  alpha,
} from "@mui/material";
import {
  CloudOutlined,
  Download,
  LockOutlined,
  OpenInNew,
  Visibility,
} from "@mui/icons-material";
import { grey } from "@mui/material/colors";
import type { AttachmentListItem } from "./types";
import { AssociationTypeEnum } from "@/graphql/generated/graphql";
import { formatBytes } from "@/utils/helperFunctions";
import DateTimeFormatter from "@/shared-components/DateTimeFormatter";

interface AttachmentRowProps {
  attachment: AttachmentListItem;
  selected: boolean;
  onSelect: () => void;
  onView: (previewUrl: string) => void;
  onOpenInNewTab: (previewUrl: string) => void;
  onDownload: (previewUrl: string) => void;
  onAssociationTypeChange?: (documentId: string, associationType: AssociationTypeEnum) => void;
}

const AttachmentRow: React.FC<AttachmentRowProps> = ({
  attachment: att,
  selected,
  onSelect,
  onView,
  onOpenInNewTab,
  onDownload,
  onAssociationTypeChange,
}) => {
  const isHard =
    (att.associationType ?? AssociationTypeEnum.Soft) === AssociationTypeEnum.Hard;

  return (
    <ListItem
      disablePadding
      onClick={onSelect}
      onDoubleClick={(e) => {
        e.preventDefault();
        onView(att.previewUrl);
      }}
      sx={{
        cursor: "pointer",
        userSelect: "none",
        border: "1px solid",
        borderColor: "transparent",
        borderRadius: 2,
        px: 2,
        "&:hover": { bgcolor: grey[200] },
        "&:hover .attachment-actions, &:focus-within .attachment-actions": {
          bgcolor: (theme) => alpha(theme.palette.background.paper, 0.72),
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
        },
        "&:hover .attachment-actions__extra, &:focus-within .attachment-actions__extra": {
          width: 64,
          opacity: 1,
          pointerEvents: "auto",
        },
        ...(selected
          ? {
            bgcolor: grey[200],
            border: "1px solid",
            borderColor: "primary.main",
            "&:hover": { bgcolor: grey[300] },
          }
          : {}),
      }}
      secondaryAction={
        <Box
          className="attachment-actions"
          component="span"
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          display="flex"
          alignItems="center"
          gap={0.5}
          sx={{
            borderRadius: 2,
            transition: (theme) =>
              theme.transitions.create("background-color", {
                duration: theme.transitions.duration.short,
              }),
          }}
        >
          <Box
            className="attachment-actions__extra"
            component="span"
            display="flex"
            sx={{
              width: 0,
              opacity: 0,
              overflow: "hidden",
              pointerEvents: "none",
              transition: (theme) =>
                theme.transitions.create(["width", "opacity"], {
                  duration: theme.transitions.duration.short,
                  easing: theme.transitions.easing.easeOut,
                }),
            }}
          >
            <Tooltip title="Download">
              <IconButton
                size="small"
                onClick={() => onDownload(att.previewUrl)}
                aria-label="Download file"
              >
                <Download fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Open in new tab">
              <IconButton
                size="small"
                onClick={() => onOpenInNewTab(att.previewUrl)}
                aria-label="Open in new tab"
              >
                <OpenInNew fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Tooltip title="Preview">
            <IconButton
              size="small"
              onClick={() => onView(att.previewUrl)}
              aria-label="Preview file"
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      }
    >
      {onAssociationTypeChange && (
        <ListItemIcon sx={{ minWidth: 36 }}>
          <Tooltip
            title={isHard ? "Hard (click to set Soft)" : "Soft (click to set Hard)"}
          >
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onAssociationTypeChange(
                  att.id,
                  isHard ? AssociationTypeEnum.Soft : AssociationTypeEnum.Hard
                );
              }}
              sx={{
                color: isHard ? "warning.main" : grey[600],
              }}
              aria-label={isHard ? "Hard link" : "Soft link"}
            >
              {isHard ? (
                <LockOutlined fontSize="small" />
              ) : (
                <CloudOutlined fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </ListItemIcon>
      )}
      <ListItemText
        primary={att.filename}
        secondary={
          <>
            {formatBytes(att.byteSize)}
            {" - "}
            {att.createdAt &&
              <DateTimeFormatter rawDate={att.createdAt}/>
            }
          </>
        }
        slotProps={{
          primary: { noWrap: true },
          secondary: { variant: "caption" },
        }}
        sx={{ minWidth: 0, maxWidth: "calc(100% - 170px)" }}
      />
    </ListItem>
  );
};

export default AttachmentRow;
